import I18n from "i18n-js";
import { arrayOf, func, shape } from "prop-types";
import React from "react";
import { connect } from "react-redux";
import Dropdown from "../../common/components/elements/Dropdown";
import Table from "../../common/components/elements/Table";
import DateInput from "../../common/components/form/DateInput";
import Select from "../../common/components/form/Select";
import List from "../../common/components/layout/List";
import { alphabetically, uniq } from "../../common/utils/filterUtils";
import { getLang, translateNomenclature } from "../../common/utils/langUtils";
import { getClientsList } from "../../company/actions/clientsActions";
import ClientUtils from "../../company/utils/ClientUtils";
import InstallationUtils from "../../installation/utils/InstallationUtils";
import { getUsersList } from "../../users/actions/usersActions";
import { getAnalysisList, markAnalysisAsViewed } from "../actions/analysisActions";
import AnalysisNature from "../models/AnalysisNature";
import StatelessCheckbox from "../../common/components/form/StatelessCheckbox";

function getAnalysisNatureOptions(analysisList) {
  return uniq(
    analysisList.map((a) => a.type),
    "nature"
  ).map((a) => a.nature);
}

function getAnalysisTypeOptions(analysisList, lang) {
  return uniq(
    analysisList.map((a) => a.type),
    "uuid"
  ).sort((a, b) => {
    const strA = translateNomenclature(a, "designation", lang);
    const strB = translateNomenclature(b, "designation", lang);
    return strA.localeCompare(strB);
  });
}

class AnalysisList extends List {
  static propTypes = {
    analysis: shape().isRequired,
    analysisList: arrayOf(shape).isRequired,
    getAnalysisList: func.isRequired,
    users: shape().isRequired,
    userProfile: shape().isRequired,
    getUsersList: func.isRequired,
    clientsList: arrayOf(shape).isRequired,
    getClientsList: func.isRequired,
    markAnalysisAsViewed: func.isRequired,
  };

  constructor() {
    super();

    this.dataTableRow = this.dataTableRow.bind(this);
    this.refreshDataState = this.refreshDataState.bind(this);
  }

  /**
   * {@inheritdoc}
   */
  componentDidMount() {
    super.componentDidMount();

    const { getAnalysisList, getUsersList, getClientsList } = this.props;

    getAnalysisList();
    getClientsList();

    if (this.shouldRenderTechFilter) {
      getUsersList();
    }
  }

  /**
   * @param {Object} prevState
   */
  refreshDataState(prevState) {
    const { client, site } = this.state.filters;
    if (!client || prevState.filters.client !== client) {
      this.setState({
        filters: {
          ...this.state.filters,
          site: null,
          installation: null,
        },
      });
    }
    if (!site || prevState.filters.site !== site) {
      this.setState({
        filters: {
          ...this.state.filters,
          installation: null,
        },
      });
    }
  }

  getKitsFromAnalyses() {
    return Array.from(
      new Set(this.props.analysisList.map((analysis) => analysis.kitBarcode))
    ).sort((a, b) => alphabetically(a, b));
  }

  getSitesFromAnalyses() {
    return uniq(
      this.props.analysisList
        .filter(this.getFilterBySelect("client"))
        .map((analysis) => analysis.site)
        .sort((a, b) => alphabetically(a.designation, b.designation)),
      "designation"
    );
  }

  getInstallationsFromAnalyses() {
    return uniq(
      this.props.analysisList
        .filter(this.getFilterBySelect("client"))
        .filter(this.getFilterBySelect("site"))
        .map((analysis) => analysis.installation)
        .sort((a, b) => alphabetically(a.designation, b.designation)),
      "designation"
    );
  }

  getServiceProvidersFromAnalyses() {
    return uniq(
      this.props.analysisList
        .filter(this.getFilterBySelect("client"))
        .filter(this.getFilterBySelect("site"))
        .filter(this.getFilterBySelect("installation"))
        .map((analysis) => analysis.serviceProvider)
        .filter(
          (provider) =>
            provider && this.props.auth.userProfile.clientUuid !== provider.uuid
        )
        .sort((a, b) =>
          alphabetically(ClientUtils.renderLabel(a), ClientUtils.renderLabel(b))
        ),
      "uuid"
    );
  }

  getStatusesFromAnalyses() {
    const { auth } = this.props;

    return uniq(
      this.props.analysisList
        .filter(this.getFilterBySelect("client"))
        .filter(this.getFilterBySelect("site"))
        .filter(this.getFilterBySelect("installation"))
        .map((analysis) => analysis.status)
        .filter(status => status !== null)
        .sort((a, b) =>
          alphabetically(
            translateNomenclature(a, "designation", auth.lang),
            translateNomenclature(b, "designation", auth.lang)
          )
        ),
      "code"
    );
  }

  getResultStatusesFromAnalyses() {
    const { auth } = this.props;
    
    return uniq(
      this.props.analysisList
        .filter(this.getFilterBySelect("client"))
        .filter(this.getFilterBySelect("site"))
        .filter(this.getFilterBySelect("installation"))
        .map((analysis) => analysis.resultStatus)
        .filter(status => status !== null)
        .sort((a, b) =>
          alphabetically(
            translateNomenclature(a, "designation", auth.lang),
            translateNomenclature(b, "designation", auth.lang)
          )
        ),
      "code"
    );
  }

  /**
   * @return {Array}
   */
  dataTableHead() {
    return [
      "",
      I18n.t("pages.analysis.list.head.date"),
      I18n.t("pages.analysis.list.head.number"),
      ...(this.shouldRenderTechFilter
        ? [I18n.t("pages.analysis.list.head.technician")]
        : []),
      I18n.t("pages.analysis.list.head.client"),
      I18n.t("pages.analysis.list.head.installation"),
      I18n.t("pages.analysis.list.head.type"),
      I18n.t("pages.analysis.list.head.status"),
      I18n.t("pages.analysis.list.head.result_status"),
      I18n.t("pages.analysis.list.head.document"),
    ];
  }

  /**
   * @param {Object} rowData
   *
   * @return {Array}
   */
  dataTableRow(rowData) {
    const { auth } = this.props;

    const {
      uuid,
      client,
      installation,
      createdAt,
      kitBarcode,
      type,
      userUuid,
      hasResultToView,
      status,
      resultStatus,
      pdfUrl,
    } = rowData;

    const hasResultToViewIndicator = hasResultToView ? (
      <div className="analysis-view-indicator">
        <span className="analysis-view-indicator-dot" />
      </div>
    ) : null;

    let technician = [];
    if (this.shouldRenderTechFilter) {
      technician = this.props.users.list.find((user) => user.uuid === userUuid);
      technician = technician
        ? [`${technician.firstname} ${technician.lastname}`]
        : [
            <span className="text-muted" key="external-operator">
              {I18n.t("pages.analysis.list.external_operator")}
            </span>,
          ];
    }

    const typeLabel = type
      ? translateNomenclature(type, "designation", auth.lang)
      : I18n.t("common.unknown");

    const statusLabel = status
      ? translateNomenclature(status, "designation", auth.lang)
      : I18n.t("common.unknown");

    const resultStatusLabel = resultStatus
      ? translateNomenclature(resultStatus, "designation", auth.lang)
      : '-';

    const analysisReport = pdfUrl ? (
      <a 
        href={pdfUrl}
        target="blank"
        key={0}
        onClick={() => {
          if (hasResultToView) {
            this.props.markAnalysisAsViewed(uuid);
          }
        }}>
        {I18n.t(`pages.analysis.list.document.analysis`)}
      </a>
    ) : null;

    return [
      hasResultToViewIndicator,
      new Date(createdAt).toLocaleDateString(getLang()),
      kitBarcode,
      ...technician,
      ClientUtils.renderLabel(client),
      InstallationUtils.renderLabel(installation),
      typeLabel,
      statusLabel,
      resultStatusLabel,
      analysisReport ? (
        <Dropdown label={`1 ${I18n.t(`pages.interventions.list.head.document`)}`}>
          {analysisReport}
        </Dropdown>
      ) : null,
    ];
  }

  /**
   * {@inheritdoc}
   */
  renderFilters() {
    const { analysisList, users, clientsList, auth } = this.props;
    const { filters } = this.state;

    const allFilters = [
      <Select
        key="filterByKit"
        id="filterByKit"
        onChange={(event) =>
          this.onChangeFilter(event, "kit", this.refreshDataState)
        }
        label={I18n.t("filters.by_kit")}
        renderOption={(option) => (
          <option key={option} value={option}>
            {option.toUpperCase()}
          </option>
        )}
        options={this.getKitsFromAnalyses()}
        defaultValue={filters.kit?.toUpperCase()}
        placeholder={I18n.t("filters.by_kit:default")}
        optional
      />,
      <Select
        key="filterByClient"
        id="filterByClient"
        onChange={(event) =>
          this.onChangeFilter(event, "client", this.refreshDataState)
        }
        label={I18n.t("filters.by_client")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {ClientUtils.renderLabel(option)}
          </option>
        )}
        options={clientsList}
        defaultValue={filters.client}
        placeholder={I18n.t("filters.by_client:default")}
        optional
      />,
      <Select
        key="filterBySite"
        id="filterBySite"
        onChange={(event) =>
          this.onChangeFilter(event, "site", this.refreshDataState)
        }
        label={I18n.t("filters.by_site")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.designation}
          </option>
        )}
        options={this.getSitesFromAnalyses()}
        defaultValue={filters.site}
        placeholder={I18n.t("filters.by_site:default")}
        optional
      />,
      <Select
        key="filterByInstallation"
        id="filterByInstallation"
        onChange={(event) => this.onChangeFilter(event, "installation")}
        label={I18n.t("filters.by_installation")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {InstallationUtils.renderLabel(option)}
          </option>
        )}
        options={this.getInstallationsFromAnalyses()}
        defaultValue={filters.installation}
        placeholder={I18n.t("filters.by_installation:default")}
        optional
      />,
      <Select
        key="filterByServiceProvider"
        id="filterByServiceProvider"
        onChange={(event) => this.onChangeFilter(event, "serviceProvider")}
        label={I18n.t("filters.by_service_provider")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {option.legalCompanyName}
          </option>
        )}
        options={this.getServiceProvidersFromAnalyses()}
        defaultValue={filters.serviceProvider}
        placeholder={I18n.t("filters.by_service_provider:default")}
        optional
      />,
      <Select
        key="filterByAnalysisNature"
        id="filterByAnalysisNature"
        onChange={(event) => this.onChangeFilter(event, "nature")}
        label={I18n.t("filters.by_analysis_nature")}
        renderOption={(option) => (
          <option key={option} value={option}>
            {I18n.t(AnalysisNature.readableFor(option))}
          </option>
        )}
        options={getAnalysisNatureOptions(analysisList)}
        defaultValue={filters.nature}
        placeholder={I18n.t("filters.by_analysis_type:default")}
        optional
      />,
      <Select
        key="filterByAnalysisType"
        id="filterByAnalysisType"
        onChange={(event) => this.onChangeFilter(event, "type")}
        label={I18n.t("filters.by_analysis_type")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {translateNomenclature(option, "designation", auth.lang)}
          </option>
        )}
        options={getAnalysisTypeOptions(analysisList, auth.lang)}
        defaultValue={filters.type}
        placeholder={I18n.t("filters.by_analysis_type:default")}
        optional
      />,
      <Select
        key="filterByAnalysisStatus"
        id="filterByAnalysisStatus"
        onChange={(event) => this.onChangeFilter(event, "status")}
        label={I18n.t("filters.by_analysis_status")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {translateNomenclature(option, "designation", auth.lang)}
          </option>
        )}
        options={this.getStatusesFromAnalyses(analysisList)}
        defaultValue={filters.type}
        placeholder={I18n.t("filters.by_analysis_status:default")}
        optional
      />,
      <Select
        key="filterByAnalysisResultStatus"
        id="filterByAnalysisResultStatus"
        onChange={(event) => this.onChangeFilter(event, "resultStatus")}
        label={I18n.t("filters.by_analysis_result_status")}
        renderOption={(option) => (
          <option key={option.uuid} value={option.uuid}>
            {translateNomenclature(option, "designation", auth.lang)}
          </option>
        )}
        options={this.getResultStatusesFromAnalyses(analysisList)}
        defaultValue={filters.type}
        placeholder={I18n.t("filters.by_analysis_result_status:default")}
        optional
      />,
      <DateInput
        key="filterByStartDate"
        id="filterByStartDate"
        onChange={(date) => this.onChangeDateFilter(date, "startDate")}
        label={I18n.t("filters.by_start_date")}
        defaultValue={filters.startDate && new Date(Number(filters.startDate))}
        legend
        optional
      />,
      <DateInput
        key="filterByEndDate"
        id="filterByEndDate"
        onChange={(date) => this.onChangeDateFilter(date, "endDate")}
        label={I18n.t("filters.by_end_date")}
        defaultValue={filters.endDate && new Date(Number(filters.endDate))}
        legend
        optional
      />,
      <StatelessCheckbox
        key="hasResultToView"
        id="hasResultToView"
        label={I18n.t("filters.result_to_view")}
        checked={Boolean(filters.hasResultToView)}
        onChangedValue={(checked) =>
          this.onChangeCheckboxFilter(checked, "hasResultToView")
        }
      />,
    ];

    if (this.shouldRenderTechFilter) {
      const userOptions = uniq(analysisList, "createdBy")
        .map((analysis) =>
          users.list.find((user) => user.uuid === analysis.createdBy)
        )
        .filter((a) => a)
        .sort((a, b) => alphabetically(a.lastname, b.lastname));

      allFilters.push(
        <Select
          key="filterByUser"
          id="filterByUser"
          defaultValue={filters.user}
          onChange={(event) => this.onChangeFilter(event, "user")}
          label={I18n.t("filters.by_user")}
          renderOption={this.renderFilterUserOption}
          options={userOptions}
          placeholder={I18n.t("filters.by_user:default")}
          optional
        />
      );
    }

    return allFilters;
  }

  getFilteredList() {
    return this.props.analysisList
      .filter(this.getFilterBySelect("client"))
      .filter(this.getFilterBySelect("site"))
      .filter(this.getFilterBySelect("installation"))
      .filter(this.getFilterByDate("createdAt"))
      .filter(this.getFilterBySelect("serviceProvider"))
      .filter(this.getFilterBySelect("user"))
      .filter(this.getFilterBySelect("status"))
      .filter(this.getFilterBySelect("resultStatus"))
      .filter(this.getFilterBySelect("type", true))
      .filter(
        this.getCustomFilter(
          "nature",
          (data, value) => data.type.nature === value
        )
      )
      .filter(
        this.getCustomFilter(
          "hasResultToView",
          (data, value) => value ? data.hasResultToView : true
        )
      )
      .filter(
        this.getCustomFilter(
          "kit",
          (data, value) => data.kitBarcode.toLowerCase() === value.toLowerCase()
        )
      );
  }

  /**
   * {@inheritdoc}
   */
  renderContent() {
    const { analysis } = this.props;

    if (analysis.inProgress) {
      return <div className="loader" />;
    }

    if (analysis.error) {
      return <p>{I18n.t("common.loadingApiError")}</p>;
    }

    return (
      <Table
        emptyContent={I18n.t("pages.analysis.list.empty")}
        data={this.getFilteredList()}
        headData={this.dataTableHead()}
        rowData={this.dataTableRow}
        getRowClasses={row => row.hasResultToView ? ["table-highlight"] : []}
        filters={this.renderFilters()}
        pagination
        onPageChanged={this.onPageChanged}
        initialPage={this.getCurrentPage()}
      />
    );
  }

  /**
   * {@inheritdoc}
   */
  renderTitle() {
    return I18n.t("pages.analysis.list.title");
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  clientsList: state.clients.list.sort((a, b) =>
    alphabetically(a.legalCompanyName, b.legalCompanyName)
  ),
  analysis: state.analysis,
  analysisList: state.analysis.list,
  users: state.users,
  userProfile: state.auth.userProfile,
});

const mapDispatchToProps = (dispatch) => ({
  getAnalysisList: () => dispatch(getAnalysisList()),
  getClientsList: () => dispatch(getClientsList()),
  getUsersList: () => dispatch(getUsersList()),
  markAnalysisAsViewed: (uuid) => dispatch(markAnalysisAsViewed(uuid)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AnalysisList);
